// ═══════════════════════════════════════════════
//   STATE — mutable globals shared across features
//   Loaded after data.js, before bootstrap.js.
//
//   Phase 3.1: this file mirrors the original monolithic state block.
//   Both coexist temporarily. Migration to S.* happens in Phase 3.2,
//   one variable per commit, verifying behavior between each.
// ═══════════════════════════════════════════════

function defaultMailDailyState() {
    return { date: null, ids: [], spamIds: [], history: [], readIds: [], trashedIds: [], repliedChoices: {} };
}

function defaultMailSpamState() {
    return { readIds: [], trashedIds: [], repliedChoices: {}, unlockedChainIds: [], importantHandled: [] };
}

const S = {
    // login bookkeeping (NEW / UPD badges in games window)
    lastLoginAt: 0,
    prevLoginAt: 0,

    // points & inventory
    pts: 0,
    myAliens: [],
    myToys: [],
    myBunnymeals: [],

    // gacha / tama UI selection
    selBall: null,
    selTama: null,

    // clowri quiz
    clowriQuizDone: false,
    clowriQuizScore: 0,

    // tutorial / first-login onboarding
    tutorialSeen: false,

    // postgame / end-of-campaign closure
    postGameSeen: false,

    // misc flags
    data_early_adopter_flag: false,

    // tama
    activeTamaUids: [],
    maxTamaSlots: 2,
    curTamaTab: 'care', // 'care' or 'storage'

    // audio
    audPlaying: false,
    audMuted: false,
    audVol: 0.1,

    // staff
    staffIdx: 0,
    staffHiredSlots: {},       // { slotIndex: applicantName } — permanently hired
    staffRejectedNames: [],    // rejected once; eligible to reapply if canReapply=true
    staffDailyDate: '',        // todayStr() of last interview session
    staffDailyCount: 0,        // interviews used today

    // daily rewards (11-day Dokomi campaign — see DAILY_CAMPAIGN in data.js)
    dailyState: {claimed: [], lastDate: '', finaleClaimed: false},

    // unlockables
    unlockedBgs: ['hills'],
    unlockedNicks: ['chronically'],
    ownedIrl: [],
    activeBg: 'hills',

    // profile
    profileData: {name: 'USER', bio: "", nick: '', traits: [], headerColors: {nameFill:'', nameStroke:'', tagBg:'', headerBg:''}},

    // missions
    missionsData: [],
    completedMissions: [],
    missionLinkOpened: {},

    // trait test
    traitAnswers: [],
    traitQIdx: 0,
    shuffledTraitQs: [],

    // bakery
    bakeryUpgrades: { speed: 0, oven: 0, grace: 0, yield: 0, decor: 0, wallpaper: 0, chef: 0, sparkle: 0 },
    bakeryOvens: [null, null, null],
    bakeryBakeCount: 0,
    bakeryUnlocked: ['cookie'],

    // games
    currentGame: 'stars',
    gamesPlayed: new Set(),
    gameTopScores: {},
    gameLastPlayed: {},

    // logbook
    logbookFavorites: [],

    // konmail
    mailFirstOpen: null,
    mailStoryRead: [],
    mailStoryChoices: {},
    mailStoryTrashed: [],
    mailDailyState: defaultMailDailyState(),
    mailImportantTrashed: [],
    mailSpamState: defaultMailSpamState(),
    // konmail v3 — calendar-locked story arc (May 18–28 2026)
    mailShopHealth:    70,
    mailClowAwareness: 0,
    mailMysteryDepth:  0,
    mailRecordsKept:   0,
    mailTrustHideo:    'mid',   // 'low' | 'mid' | 'high'
    mailTrustPetra:    'mid',
    mailLastActiveDay: 0,       // last game-day index the player was logged in (0 = before May 18)
    mailFirstLoginDay: null,    // first game-day index they ever opened the mailbox (null if pre-May 18)
    mailEmailStates:   {},      // { emailId: 'pending' | 'answered' | 'expired' | 'auto-penalised' }
    mailEnding:        null,    // ending key once Day 11 has resolved
    // Player voice counters — incremented by reply-choice effects, read by variant predicates.
    // playerVoiceClipped is double-meaningful: never resets on recovery, but `clippedRecoveryActive`
    // gates further increments after a recovery choice is taken.
    playerVoiceWarm:         0,
    playerVoiceCurious:      0,
    playerVoiceProfessional: 0,
    playerVoiceClipped:      0,
    clippedRecoveryActive:   false,
    // Story flags set by specific reply choices; read by downstream variant predicates.
    eMorrowSurfaced:     false,
    documentedPath:      false,
    hideoRouteSurfaced:  false,
    // 'opened' | 'documented' | 'left' — set by d06_coneja_package choice; drives d10 paragraph.
    packagePath:         null,
    // Writing spec v5 — drops recovery arc (Master Canon v2 §06A). Set-only when
    // the player (beat 1 or beat 2) or Coneja (beat 3) intervenes after the
    // package is opened. Drives d10_endshift opened-variant split, d10_coneja_pulled
    // arrival, and the d11 documented/closed body recolor.
    dropsPulled:         false,
    dropsPulledBy:       null,
    // Writing spec v2 §12 — staff (Coneja+Lib) report to Clow independently of the player.
    // Increments when the player misses signals the staff would notice + report.
    staffConcern:        0,
    // Idempotency set for one-time staffConcern triggers (HP crossings, deadlines,
    // per-email auto-pen events). Persisted as jsonb array; hydrated as a Set.
    staffConcernEvents:  new Set(),
    // Writing spec v2 §15 — five named investigative records. mailRecordsKept is
    // recomputed as their sum on every change.
    record_package:        false,
    record_tall_customer:  false,
    record_emorrow:        false,
    record_hideo_ps:       false,
    record_corridors:      false,
    // Writing spec v3 §19 — Hideo romance opt-in. Set-only via d04 choice C
    // (gated to trustHideo === 'high'). Drives high-trust+romance variants
    // on d06/d09, the lowercase composure-break reply, the d11 Clow p.s.
    // line, the ROMANCED logbook tag, and two conditional dossier entries.
    hideoRomance:          false,
    // Writing spec v6 §01 — Petra romance opt-in. Set-only via d07 choice B
    // (gated to trustPetra === 'high'). Drives high-trust+romance variant on
    // d09, the romance variant on d11_petra_morning, the d11 Clow p.s. line
    // (additive with hideoRomance), the ROMANCED logbook tag, and 2 dossier
    // entries. Mirrors hideoRomance shape exactly.
    petraRomance:          false,

    // window manager
    zTop: 300,

    // ── notification helpers (used progressively in Phase 3.2+) ──
    _listeners: {},
    set(key, val) {
        this[key] = val;
        const ls = this._listeners[key];
        if (ls) ls.forEach(fn => { try { fn(val); } catch (e) { console.error(e); } });
        if (typeof window.scheduleSave === 'function') window.scheduleSave();
    },
    on(key, fn) {
        (this._listeners[key] = this._listeners[key] || []).push(fn);
    }
};

window.S = S;
window.defaultMailDailyState = defaultMailDailyState;
window.defaultMailSpamState  = defaultMailSpamState;
